import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SearchRequest, SearchParameterType } from '../types';
import { apiService } from '../services/apiService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [recentSearches, setRecentSearches] = useState<SearchRequest[]>([]);
  const [parameterTypes, setParameterTypes] = useState<SearchParameterType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Load data independently to handle partial failures gracefully
      const searchPromise = apiService.getSearchHistory().catch(err => {
        console.warn('Failed to load search history:', err);
        return { results: [] }; // Return empty results for new users
      });
      
      const typesPromise = apiService.getParameterTypes().catch(err => {
        console.warn('Failed to load parameter types:', err);
        return []; // Return empty array if parameter types fail
      });
      
      const [searches, types] = await Promise.all([searchPromise, typesPromise]);
      
      // Handle both paginated and non-paginated responses
      const searchResults = searches.results || searches;
      
      setRecentSearches(searchResults.slice(0, 5)); // Show only 5 most recent
      setParameterTypes(types);
      
      // Only show error if both requests failed
      if (searchResults.length === 0 && types.length === 0) {
        setError('Unable to load dashboard data. Please refresh the page or contact support if the issue persists.');
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      
      // Check if it's a permission error (403) or authentication error (401)
      if (err instanceof Error) {
        if (err.message.includes('403') || err.message.includes('Forbidden')) {
          setError('You do not have permission to access this data. Please contact support.');
        } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          setError('Your session has expired. Please log in again.');
        } else if (err.message.includes('Network') || err.message.includes('fetch')) {
          setError('Network error. Please check your connection and try again.');
        } else {
          // For other errors, show a more user-friendly message
          setError('Unable to load some dashboard data. You can still start a new search.');
        }
      } else {
        setError('Unable to load some dashboard data. You can still start a new search.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">{status}</Badge>;
      case 'processing':
        return <Badge variant="warning">{status}</Badge>;
      case 'failed':
        return <Badge variant="error">{status}</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const formatSearchValue = (search: SearchRequest) => {
    // Check if this is a phone number search and has country code
    if (search.parameter_type_name === 'Phone Number' && search.country_code) {
      // Remove + prefix if present to avoid double plus
      const cleanCountryCode = search.country_code.startsWith('+') ? search.country_code.slice(1) : search.country_code;
      return `+${cleanCountryCode} ${search.search_value}`;
    }
    return search.search_value || 'No search value';
  };

  const getSearchIcon = (parameterTypeName: string) => {
    const icons: Record<string, string> = {
      'Phone Number': '📱',
      'Email': '📧',
      'Vehicle Number': '🚗',
      'IP Address': '🌐',
      'Website': '🌍',
      'IFSC': '🏦',
      'IMEI': '📱',
      'Company ID': '🏢',
      'Director ID': '👤',
      'Voter ID': '🗳️',
      'Individual PAN': '🆔',
      'Username': '👤',
      'Social Media Handle': '@',
      'Cryptocurrency Address': '₿'
    };
    return icons[parameterTypeName] || '🔍';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner text="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-50/40 dark:from-gray-900 dark:via-gray-850 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* 3D Holographic Welcome Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-purple-950 text-white p-6 sm:p-8 shadow-xl border border-indigo-500/20 mb-8">
          <div className="absolute right-0 top-0 bottom-0 w-full sm:w-2/5 opacity-20 sm:opacity-30 pointer-events-none overflow-hidden">
            <img 
              src="/images/3d/hero-3d.jpg" 
              alt="IntFinder 3D Intelligence" 
              className="h-full w-full object-cover object-center transform scale-110" 
            />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-3">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Live OSINT Telemetry Ready
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white mb-2">
                Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300">{user?.firstName}</span>!
              </h1>
              <p className="text-sm sm:text-base text-indigo-200/90 leading-relaxed">
                Execute cross-border OSINT queries, inspect real-time database hits, and analyze multi-source intelligence dossiers.
              </p>
            </div>
            <div className="flex flex-wrap sm:flex-nowrap gap-2.5 sm:gap-3 shrink-0">
              <Button
                variant="outline"
                size="md"
                as={Link}
                to="/app/profile"
                className="bg-white/10 hover:bg-white/20 border-white/20 text-white backdrop-blur-sm"
                icon={
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                }
              >
                Profile
              </Button>
              <Button
                variant="primary"
                size="md"
                as={Link}
                to="/app/search"
                icon={
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                }
                className="bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg shadow-indigo-500/30"
              >
                Start New Search
              </Button>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-8">
            <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/40 dark:to-yellow-950/30 shadow-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">Notice</h3>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">{error}</p>
                  {error.includes('Unable to load some dashboard data') && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                      This is normal for new users. You can still use all search features.
                    </p>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <Card className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white border-0 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-13 h-13 sm:w-14 sm:h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-md">
                  <span className="text-2xl">🪙</span>
                </div>
              </div>
              <div className="ml-4 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-indigo-100">Int Coins Balance</p>
                <p className="text-2xl sm:text-3xl font-extrabold truncate">{user?.intCoinsBalance || 0}</p>
                <p className="text-xs text-indigo-200">Available credits</p>
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white border-0 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-13 h-13 sm:w-14 sm:h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-md">
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-emerald-100">Recent Searches</p>
                <p className="text-2xl sm:text-3xl font-extrabold">{recentSearches.length}</p>
                <p className="text-xs text-emerald-200">Queries logged</p>
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-blue-600 to-cyan-700 text-white border-0 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-13 h-13 sm:w-14 sm:h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-md">
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-blue-100">Search Parameter Types</p>
                <p className="text-2xl sm:text-3xl font-extrabold">{parameterTypes.length}</p>
                <p className="text-xs text-blue-200">Supported OSINT vectors</p>
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-purple-600 to-indigo-800 text-white border-0 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-13 h-13 sm:w-14 sm:h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-md">
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-purple-100">Account Standing</p>
                <p className="text-lg sm:text-xl font-bold capitalize truncate">{user?.verificationStatus || 'Active'}</p>
                <p className="text-xs text-purple-200">Security verified</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Recent Searches - Takes 2 columns on xl screens */}
          <div className="xl:col-span-2">
            <Card
              title="Recent Searches"
              subtitle={`${recentSearches.length} searches performed`}
              headerAction={
                <Button variant="ghost" size="sm" as={Link} to="/app/search" className="text-indigo-600 hover:text-indigo-800">
                  View All →
                </Button>
              }
              className="h-full"
            >
              {recentSearches.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">No searches yet</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                    Start your first search to see results here. Your search history will appear in this section.
                  </p>
                  <Button 
                    variant="primary" 
                    as={Link} 
                    to="/app/search"
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md shadow-indigo-500/20"
                  >
                    Start Your First Search
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentSearches.map((search, index) => (
                    <div 
                      key={search.id} 
                      className="group border border-gray-200 dark:border-gray-750 rounded-xl p-4 sm:p-5 hover:bg-gradient-to-r hover:from-indigo-50/70 hover:to-purple-50/70 dark:hover:from-gray-800/80 dark:hover:to-gray-800/60 hover:border-indigo-300 dark:hover:border-indigo-700 transition-all duration-300 hover:shadow-md"
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-2xl">{getSearchIcon(search.parameter_type_name || 'Unknown Type')}</span>
                            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                              {search.parameter_type_name || 'Unknown Type'}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 break-all font-mono bg-gray-50 dark:bg-gray-900 px-3 py-2 rounded-lg border border-gray-200/50 dark:border-gray-700/50">
                            {formatSearchValue(search)}
                          </p>
                          <div className="flex items-center flex-wrap gap-4 text-xs">
                            <span className="flex items-center text-gray-500 dark:text-gray-400">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                              {search.total_cost || 0} Int Coins
                            </span>
                            <span className="flex items-center text-gray-500 dark:text-gray-400">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {search.created_at ? new Date(search.created_at).toLocaleDateString() : 'Unknown date'}
                            </span>
                            {search.parameter_type_name === 'Phone Number' && search.country_code && (
                              <span className="flex items-center text-indigo-600 dark:text-indigo-400 font-medium">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {search.country_code.startsWith('+') ? search.country_code : `+${search.country_code}`}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="ml-2 sm:ml-4 flex-shrink-0">
                          {getStatusBadge(search.status || 'unknown')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Quick Actions - Takes 1 column on xl screens */}
          <div className="xl:col-span-1">
            <Card
              title="Quick Actions"
              subtitle="Get started with common tasks"
              className="h-full"
            >
              <div className="space-y-4">
                <Button
                  variant="primary"
                  className="w-full justify-center bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md shadow-indigo-500/20"
                  as={Link}
                  to="/app/search"
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  }
                >
                  Start New Search
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-center border-indigo-200 text-indigo-700 hover:bg-indigo-50 dark:bg-indigo-900/20 hover:border-indigo-300"
                  as={Link}
                  to="/app/subscriptions"
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  }
                >
                  Add Int Coins
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-center border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:border-gray-600"
                  as={Link}
                  to="/app/profile"
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  }
                >
                  View Profile
                </Button>

                {user?.isStaff && (
                  <Button
                    variant="outline"
                    className="w-full justify-center border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300"
                    as={Link}
                    to="/app/admin/users"
                    icon={
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                    }
                  >
                    Admin Panel
                  </Button>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}; 